import Icon from '@/components/ui/AppIcon';

interface Requirement {
  platform: string;
  icon: string;
  specs: string[];
  size: string;
  version: string;
}

const requirements: Requirement[] = [
  {
    platform: 'Android',
    icon: 'DevicePhoneMobileIcon',
    specs: [
      'Android 8.0 or higher',
      'Minimum 2GB RAM',
      'Internet connection for streaming',
      'Storage space for offline downloads'
    ],
    size: '45 MB',
    version: 'v3.2.1'
  },
  {
    platform: 'iOS',
    icon: 'DevicePhoneMobileIcon',
    specs: [
      'iOS 13.0 or later',
      'Compatible with iPhone, iPad, iPod touch',
      'Internet connection for streaming',
      'Storage space for offline downloads'
    ],
    size: '52 MB',
    version: 'v3.2.1'
  }
];

export default function SystemRequirements() {
  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-4">
            System Requirements &<br />
            <span className="text-primary">Compatibility</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Ensure your device meets the minimum requirements for optimal performance
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {requirements.map((req, index) => (
            <div
              key={index}
              className="bg-card p-8 rounded-2xl border border-border hover:border-primary transition-all duration-300 hover:shadow-warm-lg"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center">
                  <Icon name={req.icon as any} size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-text-primary">
                    {req.platform}
                  </h3>
                  <div className="flex items-center space-x-4 text-sm text-text-secondary mt-1">
                    <span className="flex items-center space-x-1">
                      <Icon name="ArrowDownTrayIcon" size={16} />
                      <span>{req.size}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="CodeBracketIcon" size={16} />
                      <span>{req.version}</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {req.specs.map((spec, specIndex) => (
                  <div key={specIndex} className="flex items-start space-x-3">
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      variant="solid"
                      className="text-success mt-0.5 flex-shrink-0"
                    />
                    <span className="text-text-secondary">{spec}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-2xl max-w-5xl mx-auto">
          <div className="flex items-start space-x-4">
            <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
            <div>
              <h4 className="text-lg font-heading font-bold text-text-primary mb-2">
                Performance Tips
              </h4>
              <ul className="space-y-2 text-text-secondary">
                <li>• For best audio quality, use headphones or external speakers</li>
                <li>• Enable background playback in app settings for uninterrupted listening</li>
                <li>• Download stories on Wi-Fi to save mobile data</li>
                <li>• Clear cache regularly to maintain optimal app performance</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}